import { useCallback, useEffect, useRef, useState } from "react";
import GuestService from "../../../services/guest.service";
import { isPendingKeysReport, reconcileKeysReport } from "../../../common/keysReport";

export const REPORT_TIMEOUT_MS = 60000;

export default function useKeysReportRefresh({ bookingId, hotel, report, setReport, setFormValues }) {
  const reportRef = useRef(report);
  reportRef.current = report;
  const hotelRef = useRef(hotel);
  hotelRef.current = hotel;
  const [refreshVersion, setRefreshVersion] = useState(0);
  const refresh = useCallback(() => setRefreshVersion(value => value + 1), []);
  const watching = isPendingKeysReport(report, hotel);
  const requestId = report?.requestId;
  useEffect(() => {
    const currentHotel = hotelRef.current;
    if (!bookingId || !currentHotel?._id || !watching) return undefined;
    let active = true, fetching = false, queued = false, timedOut = false;
    let poll;
    const update = transform => setReport(previous =>
      previous?.requestId === requestId ? transform(previous) : previous,
    );
    update(previous => ({ ...previous, timedOut: false }));
    const timeout = setTimeout(() => {
      timedOut = true;
      clearTimeout(poll);
      update(previous => ({ ...previous, timedOut: true }));
    }, REPORT_TIMEOUT_MS);
    const read = async () => {
      if (fetching) { queued = true; return; }
      fetching = true;
      clearTimeout(poll);
      try {
        const current = reportRef.current;
        const { data } = await GuestService.getBooking(bookingId,
          current?.deliveryId ? { keyReportId: current.deliveryId } : {},
        );
        if (!active) return;
        setFormValues(previous => String(previous._id) === String(bookingId)
          ? { ...previous, passcodes: data.passcodes || [], ekeys: data.ekeys || [] }
          : previous);
        update(previous => reconcileKeysReport(previous, data, currentHotel, bookingId));
      } catch {
        if (active) update(previous => ({ ...previous, confirmationError: true }));
      } finally {
        fetching = false;
        if (active && queued) { queued = false; read(); }
        else if (active && !timedOut) poll = setTimeout(read, 2000);
      }
    };
    const changed = ({ detail }) => {
      if (String(detail?.hotelId) !== String(currentHotel._id)) return;
      if (detail?.refreshAll || detail?.bookingIds?.some(id => String(id) === String(bookingId)) ||
          reportRef.current?.passcodesDoors?.some(row => String(row.code?._id) === String(detail?.passcodeId))) read();
    };
    window.addEventListener("loxe:schlage-passcode", changed);
    read();
    return () => {
      active = false;
      clearTimeout(poll);
      clearTimeout(timeout);
      window.removeEventListener("loxe:schlage-passcode", changed);
    };
  }, [bookingId, hotel?._id, hotel?.lockType, requestId, watching, refreshVersion, setReport, setFormValues]);
  return refresh;
}
